import { Box, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { RobotActions } from '../../../utils/constants';
import { clampPercent } from '../../../utils/programDeployProgressUtils';

const FirmwareUpdatingDialog = (props) => {
  const { robotTypeName } = props;
  const { t } = useTranslation();
  const { progress, firmwareState } = useSelector((state) => state.robot.firmware);
  const progressState = typeof progress?.state === 'string' ? progress.state : '';
  const isFinalizingFirmware =
    firmwareState === RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES ||
    firmwareState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_PROGRESS ||
    firmwareState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_SUCCEEDED;
  const isTerminalProgressState =
    progressState === 'idle' ||
    firmwareState === RobotActions.FIRMWARE_UPDATE.SUCCEEDED ||
    firmwareState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_SUCCEEDED;

  const rawProgressPercent =
    typeof progress === 'number' ? progress : typeof progress?.percent === 'number' ? progress.percent : isTerminalProgressState ? 100 : 0;
  const progressPercent = clampPercent(rawProgressPercent);
  const assetLabel = progress?.assetId || progress?.libraryId;
  const progressStateLabel =
    progressState === 'checking'
      ? t('connectedDrawer.firmware-transfer-state.checking', 'Checking')
      : progressState === 'downloading'
      ? t('connectedDrawer.firmware-transfer-state.downloading', 'Downloading')
      : progressState === 'sending'
      ? t('connectedDrawer.firmware-transfer-state.sending', 'Transferring')
      : progressState === 'idle'
      ? t('connectedDrawer.firmware-transfer-state.idle', 'Complete')
      : undefined;

  const progressLabel = isFinalizingFirmware
    ? t('firmware.update.finalization_progress_label', 'Finalizing firmware update')
    : assetLabel && progressStateLabel
    ? t('connectedDrawer.firmware-transfer-asset-state', {
        asset: assetLabel,
        state: progressStateLabel,
        defaultValue: '{{asset}} ({{state}})',
      })
    : assetLabel || t('firmware.update.preparing', 'Preparing firmware update');

  return (
    <>
      <DialogTitle variant="sectionTitle" sx={{ textAlign: 'center' }}>
        <Trans i18nKey="firmware.update.critical_title">Critical Firmware Update</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={4} sx={{ justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Typography align="center" variant="body1">
            <Trans i18nKey="firmware.update.critical_information" values={{ robotTypeName }}>
              Hang in there while your {{ robotTypeName }} is updating. Keep your {{ robotTypeName }} plugged in with the USB cable and do not power
              it off. Stay on the Blueprint Software website and do not close your browser until the update is complete.
            </Trans>
          </Typography>
          <Box sx={{ width: '100%', maxWidth: '420px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {progressLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${progressPercent.toFixed(1)}%`}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 4, borderRadius: 2 }} />
          </Box>
        </Stack>
      </DialogContent>
    </>
  );
};

FirmwareUpdatingDialog.propTypes = {
  robotTypeName: PropTypes.string,
};

export default FirmwareUpdatingDialog;
